/*
 * Pure helpers behind the Domains index metrics.
 *
 * Kept free of React and router imports so they can be unit-tested directly —
 * CRA's Jest cannot resolve react-router-dom v7's ESM exports map, so anything
 * reachable from Domains.js is untestable.
 */

/* The backend buckets both hits and impressions by MST calendar day, so the
   x-axis has to be built in MST too — using the browser's local date (or UTC)
   shifts every key by a day for part of each day. */
export const mstDayKey = (date) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Phoenix',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);

/* The 30 complete MST days ending yesterday — matching the window the
   refresh-page-hits query uses, so today's partial day never shows as a dip. */
export const mstDayWindow = (days = 30, now = new Date()) => {
  const keys = [];
  for (let i = days; i >= 1; i--) {
    keys.push(mstDayKey(new Date(now.getTime() - i * 86400000)));
  }
  return keys;
};

/* dailyMap values are either a plain count (hits) or { impressions, clicks }. */
export const dailyToSeries = (dailyMap, dayKeys) => {
  if (!dailyMap || typeof dailyMap !== 'object') return [];
  return dayKeys.map((key) => {
    const entry = dailyMap[key];
    if (entry == null) return 0;
    if (typeof entry === 'number') return entry;
    return entry.impressions || 0;
  });
};

/* Snowplow keys page hits by raw page_urlhost and AdSense keys earnings by
   DOMAIN_NAME, so one site shows up as BOTH "example.com" and "www.example.com".
   Ad impressions are already collapsed onto the apex form by the backend, so
   the other two maps have to be collapsed the same way before anything is
   compared or divided.  Taking the first match instead of summing dropped 671
   of spectaculartoys.com's 731 hits, which would have read as 13 ads per view. */
export const normalizeHost = (host) => {
  const h = String(host || '').toLowerCase();
  return h.startsWith('www.') ? h.slice(4) : h;
};

/* { "www.example.com": 5, "example.com": 700 } -> { "example.com": 705 } */
export const sumByBaseHost = (map) => {
  const out = {};
  Object.entries(map || {}).forEach(([key, value]) => {
    const base = normalizeHost(key);
    out[base] = (out[base] || 0) + (Number(value) || 0);
  });
  return out;
};

/* Same collapse, but for the per-day maps: { host: { day: count } }. */
export const mergeDailyByBaseHost = (map) => {
  const out = {};
  Object.entries(map || {}).forEach(([key, daily]) => {
    const base = normalizeHost(key);
    const target = out[base] || (out[base] = {});
    Object.entries(daily || {}).forEach(([day, value]) => {
      target[day] = (target[day] || 0) + (Number(value) || 0);
    });
  });
  return out;
};

/* Sum a { day: count } map over an explicit list of days, so a total always
   covers the same window as the sparkline beside it. */
export const sumOverDays = (dailyMap, dayKeys) =>
  dayKeys.reduce((acc, key) => {
    const entry = (dailyMap || {})[key];
    if (entry == null) return acc;
    return acc + (typeof entry === 'number' ? entry : (entry.impressions || 0));
  }, 0);

/* ──────────── content-health ("needs attention") helpers ──────────── */

/* The three per-domain counters refresh-page-stats stores alongside
   page_count.  Missing (older, un-refreshed) values read as 0 so a domain
   never shows as needing attention just because its stats are stale. */
export const attentionCounts = (domain) => ({
  drafts: Number(domain?.pages_draft) || 0,
  noDesc: Number(domain?.pages_missing_description) || 0,
  imgErr: Number(domain?.pages_image_error) || 0,
});

export const needsAttention = (domain) => {
  const { drafts, noDesc, imgErr } = attentionCounts(domain);
  return drafts > 0 || noDesc > 0 || imgErr > 0;
};

/* Compact badge parts for the Attention column: [{ key, count, label }].
   Only non-zero counters are returned, so the column can render "—" for a
   clean domain and "3 drafts · 1 img err" for a messy one. */
export const attentionBadges = (domain) => {
  const { drafts, noDesc, imgErr } = attentionCounts(domain);
  const out = [];
  if (drafts > 0) out.push({ key: 'drafts', count: drafts, label: drafts === 1 ? 'draft' : 'drafts' });
  if (noDesc > 0) out.push({ key: 'noDesc', count: noDesc, label: 'no desc' });
  if (imgErr > 0) out.push({ key: 'imgErr', count: imgErr, label: imgErr === 1 ? 'img err' : 'img errs' });
  return out;
};

export const attentionSummary = (domain) =>
  attentionBadges(domain).map((b) => `${b.count} ${b.label}`).join(' · ');

/* Fold the KPI strip totals over whatever list the table is showing, using
   the same per-domain accessors the columns use so tiles and footer agree.
   eCPM is averaged over ad-tracked domains only (see the tfoot comment);
   RPM likewise over domains with page views, so earnings with no hits to
   divide by are not charged against other domains' traffic. */
export const kpiTotals = (domains, { hits, earnings, impressions }) => {
  let totalHits = 0;
  let totalEarnings = 0;
  let trackedEarnings = 0;
  let trackedImpr = 0;
  let viewedEarnings = 0;
  let viewedHits = 0;
  let scheduled = 0;
  let newThisMonth = 0;
  let drafts = 0;
  (domains || []).forEach((d) => {
    const h = hits(d);
    const e = earnings(d);
    const i = impressions(d);
    totalHits += h;
    totalEarnings += e;
    if (i > 0) {
      trackedEarnings += e;
      trackedImpr += i;
    }
    if (h > 0) {
      viewedEarnings += e;
      viewedHits += h;
    }
    scheduled += Number(d.pages_scheduled) || 0;
    newThisMonth += Number(d.pages_created_this_month) || 0;
    drafts += Number(d.pages_draft) || 0;
  });
  return {
    hits: totalHits,
    earnings: totalEarnings,
    ecpm: trackedImpr > 0 ? (trackedEarnings / trackedImpr) * 1000 : null,
    rpm: viewedHits > 0 ? (viewedEarnings / viewedHits) * 1000 : null,
    scheduled,
    newThisMonth,
    drafts,
  };
};

/* ──────────── landing-page helpers (round two) ──────────── */

/* "3h ago" style freshness text.  Returns '' for a missing or unparseable
   timestamp so callers can simply omit the line.  A timestamp in the future
   (clock skew) reads as "just now" rather than a negative age. */
export const relativeTime = (iso, now = new Date()) => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const seconds = Math.floor((nowMs - then.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/* The newest value of an ISO-timestamp field across a list — used for
   "last stats refresh" since page_stats_updated_at is stored per domain. */
export const latestTimestamp = (items, field) => {
  let best = null;
  (items || []).forEach((item) => {
    const value = item && item[field];
    if (!value) return;
    const ms = new Date(value).getTime();
    if (Number.isNaN(ms)) return;
    if (best == null || ms > best.ms) best = { ms, value };
  });
  return best ? best.value : null;
};

/* Group the scheduled-pages feed into ordered day buckets:
   [{ day: 'YYYY-MM-DD' | null, pages: [...] }], days ascending, pages inside
   each day ascending by published_at.  Days are MST calendar days to match
   the rest of the console.  Pages with no parseable published_at land in a
   trailing { day: null } bucket rather than being silently dropped. */
export const groupScheduledByDay = (pages) => {
  const buckets = new Map();
  const undated = [];
  (pages || []).forEach((page) => {
    const ms = page && page.published_at ? new Date(page.published_at).getTime() : NaN;
    if (Number.isNaN(ms)) {
      undated.push(page);
      return;
    }
    const day = mstDayKey(new Date(ms));
    if (!buckets.has(day)) buckets.set(day, []);
    buckets.get(day).push({ page, ms });
  });
  const out = Array.from(buckets.keys())
    .sort()
    .map((day) => ({
      day,
      pages: buckets.get(day).sort((a, b) => a.ms - b.ms).map((e) => e.page),
    }));
  if (undated.length > 0) out.push({ day: null, pages: undated });
  return out;
};

/* Human day header for a bucket: "Today", "Tomorrow", else "Mon, Sep 14".
   todayKey is injected so the label is testable and stays MST-consistent. */
export const dayLabel = (dayKey, todayKey) => {
  if (!dayKey) return 'Unscheduled';
  if (dayKey === todayKey) return 'Today';
  if (todayKey) {
    const next = new Date(`${todayKey}T12:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    if (dayKey === next.toISOString().slice(0, 10)) return 'Tomorrow';
  }
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

/* The n domains most in need of attention, worst first; ties break on
   hostname so the list is stable between renders.  Clean domains are never
   included, so the result may be shorter than n. */
export const topAttentionDomains = (domains, n = 8) => {
  const total = (d) => {
    const { drafts, noDesc, imgErr } = attentionCounts(d);
    return drafts + noDesc + imgErr;
  };
  return (domains || [])
    .filter(needsAttention)
    .sort((a, b) =>
      total(b) - total(a) ||
      String(a.hostname || '').localeCompare(String(b.hostname || '')))
    .slice(0, n);
};

